/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AttributesForm.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import ViewAttributeList from "./ViewAttributeList";
import FileUploadDeloitteFormate from "../../../components/file-upload/FileUploadDeloitteFormate";

const attributeValueShape = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.shape({
    filename: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }),
]);

const attributeShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  attribute_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  attribute_type: PropTypes.string,
  attribute_sub_type: PropTypes.string,
  attribute_heading: PropTypes.string,
  attribute_value: attributeValueShape,
  newlyAdded: PropTypes.bool,
  edited: PropTypes.bool,
});


const normalizeAttributeList = (items = []) =>
  Array.isArray(items)
    ? items.map((att) => ({
      ...att,
      attribute_id: att?.attribute_id ?? att?.id ?? null,
      newlyAdded: Boolean(att?.newlyAdded),
      edited: Boolean(att?.edited),
    }))
    : [];

const resolveFileName = (fileValue) => {
  if (!fileValue) return "";
  if (typeof fileValue === "string") return fileValue.trim();
  return fileValue?.filename || fileValue?.name || fileValue?.url || "";
};


const normalizeValueForCompare = (value) => {
  if (value == null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

function AttributesForm({
  type,
  attributeFromApi = [],
  getFormData = () => { },
  onCopyAttribute = () => { },
  reason,
  setReason,
  reasonError,
  setReasonError = () => { },
  showReason = false,
}) {
  const [addFieldType, setAddFieldType] = useState("TEXT");
  const [addSubFieldType, setAddSubFieldType] = useState("");
  const [addHeadder, setAddHeadder] = useState("");
  const [addFieldValue, setAddFieldValue] = useState("");
  const [attributeList, setAttributeList] = useState(() =>
    normalizeAttributeList(attributeFromApi),
  );
  const [deletedAttributeList, setDeletedAttributeList] = useState([]);
  const disableField = type === "VIEW" || type === "COPY";
  const showReasonField = showReason;
  const getFormDataRef = useRef(getFormData);

  const attributeSignature = JSON.stringify(attributeFromApi ?? []);


  useEffect(() => {
    const nextList = normalizeAttributeList(attributeFromApi);
    setAttributeList((prevList) => {
      const sameList =
        prevList.length === nextList.length &&
        prevList.every(
          (item, index) =>
            JSON.stringify(item) === JSON.stringify(nextList[index]),
        );
      return sameList ? prevList : nextList;
    });
    setDeletedAttributeList([]);
  }, [attributeSignature, type]);

  useEffect(() => {
    getFormDataRef.current = getFormData;
  }, [getFormData]);

  useEffect(() => {
    const added = attributeList
      .filter((item) => item.newlyAdded && !item.attribute_id)
      .map((FilterItem) => ({
        attribute_type: FilterItem.attribute_type,
        attribute_sub_type: FilterItem.attribute_sub_type
          ? FilterItem.attribute_sub_type.toUpperCase()
          : null,
        attribute_heading: FilterItem.attribute_heading,
        attribute_value: FilterItem.attribute_value,
      }));
    const edited = attributeList
      .filter((item) => item.edited && !item.newlyAdded && item.attribute_id)
      .map((FilterItem) => ({
        id: FilterItem.attribute_id,
        attribute_type: FilterItem.attribute_type,
        attribute_sub_type: FilterItem.attribute_sub_type
          ? FilterItem.attribute_sub_type.toUpperCase()
          : null,
        attribute_heading: FilterItem.attribute_heading,
        attribute_value: FilterItem.attribute_value,
      }));

    getFormDataRef.current({
      added,
      edited,
      deleted: deletedAttributeList,
    });
  }, [attributeList, deletedAttributeList]);


  const resetAddForm = () => {
    setAddFieldType("TEXT");
    setAddSubFieldType("");
    setAddHeadder("");
    setAddFieldValue("");
  };

  const handleAddAttribute = () => {
    const normalizedAttributeValue =
      addFieldType === "FILE" ? resolveFileName(addFieldValue) : addFieldValue;

    const newAttribute = {
      attribute_id: null,
      attribute_type: addFieldType,
      attribute_sub_type:
        addFieldType === "FILE" ? addSubFieldType?.trim() : null,
      attribute_heading: addHeadder.trim(),
      attribute_value: normalizedAttributeValue,
      newlyAdded: true,
      edited: false,
    };

    setAttributeList((prev) => [...prev, newAttribute]);
    resetAddForm();
  };

  const handleUpdateAttribute = (index, value) => {
    const itemToUpdate = attributeList[index];
    if (!itemToUpdate) return;
    
    const attributeId = itemToUpdate?.attribute_id ?? itemToUpdate?.id ?? null;
    const originalAttribute = attributeFromApi.find(
      (attribute) =>
        String(attribute?.attribute_id ?? attribute?.id ?? "") ===
        String(attributeId ?? ""),
    );

    const isChanged =
      !itemToUpdate.newlyAdded &&
      normalizeValueForCompare(value) !==
        normalizeValueForCompare(originalAttribute?.attribute_value);

    setAttributeList((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        return {
          ...item,
          attribute_value: value,
          edited: Boolean(attributeId) && isChanged,
        };
      }),
    );
  };

  const handleDeleteAttribute = (index) => {
    const removedItem = attributeList[index];
    const removedAttributeId =
      removedItem?.attribute_id ?? removedItem?.id ?? null;
    if (removedAttributeId) {
      setDeletedAttributeList((current) =>
        current.includes(removedAttributeId)
          ? current
          : [...current, removedAttributeId],
      );
    }
    setAttributeList((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };


  const isHeadingValid = addHeadder.trim().length >= 3;

  const isValueValid = (() => {
    if (addFieldType === "FILE")
      return Boolean(addFieldValue?.url || addFieldValue?.filename);
    if (addFieldType === "NUMBER")
      return addFieldValue.trim().length >= 1 &&
        ![".", "-", "-."].includes(addFieldValue.trim()); // ← added check
    return addFieldValue.trim().length >= 1;
  })();

  const addDisabled =
    !addFieldType ||
    !isHeadingValid ||
    (addFieldType === "FILE" && !addSubFieldType) ||
    !isValueValid;


  return (
    <Box>
      <Stack
        spacing={2}
        sx={{
          width: "100%",
          maxWidth: "450px",
          boxSizing: "border-box",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: "0 0 auto" }}>Attribute fields</Box>

        {(type === "ADD" || type === "EDIT") && (
          <Box sx={{ flex: "0 0 auto" }}>
            <CustomSelectV2
              size={"small"}
              label={"Form Type"}
              optionsList={["TEXT", "NUMBER", "FILE"].map((e) => ({
                value: e,
                label: e,
              }))}
              required={true}
              selectedValue={addFieldType}
              handleOnChange={(e) => {
                setAddFieldType(e.target.value);
                setAddSubFieldType("");
                setAddFieldValue("");
              }}
            />

            {addFieldType === "FILE" && (
              <CustomSelectV2
                size={"small"}
                label={"Document Type"}
                optionsList={["XLS", "CSV", "XML", "PDF"].map((e) => ({
                  value: e.toLowerCase(),
                  label: e,
                }))}
                required={true}
                selectedValue={addSubFieldType}
                handleOnChange={(e) => {
                  setAddSubFieldType(
                    String(e.target.value).trim().toLowerCase(),
                  );
                  setAddFieldValue("");
                }}
              />
            )}

            <CustomFormTextFieldV2
              size={"small"}
              label={"Heading"}
              required={true}
              type={"text"}
              fldType={"textO"}
              value={addHeadder}
              setValue={(value) => {
                if (value.length <= 40) setAddHeadder(value);
              }}
              maxLength={40}
            />

            {addFieldType === "FILE" ? (
              <FileUploadDeloitteFormate
                value={addFieldValue}
                onChange={(uploadedFile) => setAddFieldValue(uploadedFile)}
                allowedExtensions={
                  addSubFieldType ? [addSubFieldType.trim().toLowerCase()] : []
                }
                disabled={addSubFieldType === ""}
              />
            ) : (
              <Box>
                <Typography
                  sx={{
                    mb: 0.5,
                    color: "#6f7174",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    textAlign: "left",
                  }}
                >
                  Value <span style={{ color: "red" }}>*</span>
                </Typography>
                <CustomFormTextFieldV2
                  size={"small"}
                  label=""
                  required={false}
                  type={
                    addFieldType === "NUMBER"
                      ? "text"
                      : addFieldType.toLowerCase()
                  }
                  inputMode={addFieldType === "NUMBER" ? "numeric" : undefined}
                  fldType={addFieldType === "NUMBER" ? "number" : ""} // Use "number" fldType for better formatting and validation

                  value={addFieldValue ?? ""}
                  setValue={(value) => {
                    if (addFieldType === "NUMBER") {
                      let raw = String(value ?? "");

                      let cleaned = raw.replaceAll(/[^0-9.-]/g, "");

                      const hasLeadingMinus = cleaned.startsWith("-");
                      cleaned = cleaned.replaceAll("-", "");
                      if (hasLeadingMinus) cleaned = "-" + cleaned;

                      const parts = cleaned.split(".");
                      if (parts.length > 2) {
                        cleaned = parts[0] + "." + parts.slice(1).join("");
                      }

                      setAddFieldValue(cleaned);
                    } else {
                      setAddFieldValue(value);
                    }
                  }}
                  maxLength={40}
                />
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <CustomButtonV2
                size="small"
                disabled={addDisabled}
                btnName={"Add"}
                handleOnClick={handleAddAttribute}
              />
            </Box>

            {showReasonField && (
              <CustomFormTextFieldV2
                size={"small"}
                label={"Reason"}
                required={true}
                type={"text"}
                fldType={"text"}
                value={reason || ""}
                setValue={(value) => value?.length <= 100 && setReason(value)}
                valueError={reasonError}
                setValueError={setReasonError}
                maxLength={100}
              />
            )}
          </Box>
        )}

        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "auto",
            display: "flex",
          }}
        >
          <ViewAttributeList
            attributeList={attributeList}
            onUpdateAttribute={handleUpdateAttribute}
            onDeleteAttribute={handleDeleteAttribute}
            onCopyAttribute={onCopyAttribute}
            type={type}
            disableField={disableField}
          />
        </Box>
      </Stack>
    </Box>
  );
}

AttributesForm.propTypes = {
  type: PropTypes.oneOf(["ADD", "EDIT", "VIEW", "COPY"]),
  attributeFromApi: PropTypes.arrayOf(attributeShape),
  getFormData: PropTypes.func,
  onCopyAttribute: PropTypes.func,
  reason: PropTypes.string,
  setReason: PropTypes.func,
  reasonError: PropTypes.string,
  setReasonError: PropTypes.func,
  showReason: PropTypes.bool,
};

export default AttributesForm;
